import React, { useCallback, useMemo } from "react";
import {
  List,
  ListItemText,
  Typography,
  Paper,
  Box,
  ListItemButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { Icon } from "../icons";

// 애니메이션 Keyframes
const animations = `
  @keyframes blinking {
    0% { opacity: 1; }
    50% { opacity: 0.4; }
    100% { opacity: 1; }
  }
  @keyframes slideInFromLeft {
    from { transform: translateX(-110%); }
    to { transform: translateX(0); }
  }
  @keyframes pulse {
    0% { background-color: rgba(255, 159, 67, 0.4); }
    50% { background-color: rgba(255, 159, 67, 0.7); }
    100% { background-color: rgba(255, 159, 67, 0.4); }
  }
`;

// 컴포넌트 전용 커스텀 다크 테마 객체
const customDarkTheme = {
  palette: {
    divider: "rgb(225 222 245 / 0.12)",
    text: {
      primary: "rgb(225 222 245 / 0.9)",
      secondary: "rgb(225 222 245 / 0.7)",
    },
    success: { main: "#28C76F" },
    warning: { main: "#FF9F43" },
  },
  shape: { borderRadius: 6 },
  spacing: (factor) => `${8 * factor}px`,
  shadows: {
    10: "0px 6px 6px -3px rgba(0,0,0,0.2),0px 10px 14px 1px rgba(0,0,0,0.14),0px 4px 18px 3px rgba(0,0,0,0.12)",
  },
};

const FloatingPanel = styled(Paper)(() => ({
  position: "fixed",
  top: "0px",
  left: "80px", // 80px 너비의 사이드바 바로 옆에 위치
  width: "350px", // 너비 350px로 변경
  height: "100%", // 높이를 화면에 맞게 조정 (상하 여백 20px씩)
  zIndex: 1400,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  borderRadius: 0, // radius 제거
  border: `solid 1px #3F4463`, // 테두리 두께 조정
  backgroundColor: "transparent",
  boxShadow: customDarkTheme.shadows[5], // 그림자 효과 약하게 조정
  animation: "slideInFromLeft 0.5s ease-out forwards",
  willChange: "transform", // 성능 최적화 힌트
}));

const PanelHeader = styled(Box)(() => ({
  padding: customDarkTheme.spacing(2, 3),
  borderBottom: `1px solid ${customDarkTheme.palette.divider}`,
  backgroundColor: "rgba(47, 51, 73, 0.9)",
}));

const PanelContent = styled(Box)(() => ({
  flexGrow: 1, // 남은 공간을 모두 차지하도록 설정
  padding: customDarkTheme.spacing(2, 3),
  overflowY: "auto",
  backgroundColor: "rgba(47, 51, 73, 0.9)",
  // 스크롤바 스타일링 (선택적)
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "rgba(255,255,255,0.2)",
    borderRadius: "4px",
  },
}));

const StyledListItemButton = styled(ListItemButton, {
  shouldForwardProp: (prop) => prop !== "checked",
})(({ checked }) => {
  let styles = {};
  switch (checked) {
    case 1: // 처리중
      styles = {
        backgroundColor: "rgba(255, 159, 67, 0.4)",
        border: "1px solid rgba(255, 159, 67, 0.8)",
        animation: "pulse 2s infinite",
        willChange: "background-color", // 성능 최적화 힌트
        "&:hover": {
          backgroundColor: "rgba(255, 159, 67, 0.6)",
        },
      };
      break;
    case 2: // 조치완료
      styles = {
        backgroundColor: "rgba(115, 103, 240, 0.2)",
        border: "1px solid rgba(115, 103, 240, 0.8)",
        "&:hover": {
          backgroundColor: "rgba(115, 103, 240, 0.3)",
        },
      };
      break;
    case 0: // 미처리
    default:
      styles = {
        backgroundColor: "rgba(244, 67, 54, 0.3)",
        border: "1px solid rgba(244, 67, 54, 0.8)",
        "&:hover": {
          backgroundColor: "rgba(244, 67, 54, 0.5)",
        },
      };
      break;
  }
  return {
    marginBottom: customDarkTheme.spacing(1),
    borderRadius: customDarkTheme.shape.borderRadius,
    transition: "background-color 0.3s, border 0.3s",
    ...styles,
  };
});

// 개별 리스트 아이템 컴포넌트 (메모이제이션 적용)
const ChecklistItem = React.memo(({ item, onCheck }) => {
  const handleClick = useCallback(() => {
    // 0 -> 1 -> 2 -> 0 순환 로직은 부모가 아닌 여기서 계산하거나, 부모에게 위임
    // 여기서는 onCheck에 현재 상태를 넘겨서 부모가 처리하도록 유지
    onCheck(item.step_id, item.is_checked);
  }, [item.step_id, item.is_checked, onCheck]);

  return (
    <StyledListItemButton checked={item.is_checked} onClick={handleClick}>
      <ListItemText
        primary={
          <Typography
            variant="body1"
            sx={{
              color:
                item.is_checked === 2
                  ? customDarkTheme.palette.text.primary
                  : item.is_checked === 1
                  ? customDarkTheme.palette.warning.main
                  : "#f44336",
              fontWeight: item.is_checked === 2 ? "normal" : "bold",
              textDecoration: item.is_checked === 2 ? "line-through" : "none",
            }}
          >
            {`${item.index}. ${item.response}`}
          </Typography>
        }
        secondary={
          <>
            <Typography
              variant="body2"
              sx={{ color: customDarkTheme.palette.text.secondary }}
              component="span"
              display="block"
            >
              {`담당: ${item.manager} / 책임: ${item.supervisor} / 부서: ${item.coop_department}`}
            </Typography>
            {item.is_checked === 2 && (
              <Typography
                variant="caption"
                sx={{ color: customDarkTheme.palette.text.secondary }}
                component="span"
                display="block"
              >
                {`(완료: ${item.checked_by || "확인 안됨"} / ${new Date(
                  item.checked_at
                ).toLocaleTimeString()})`}
              </Typography>
            )}
          </>
        }
      />
    </StyledListItemButton>
  );
}, (prevProps, nextProps) => {
  // props 비교 함수 (필요한 경우 커스텀 비교 로직 추가)
  // item 객체가 불변성을 유지하며 업데이트된다면 얕은 비교로 충분함
  return prevProps.item === nextProps.item && prevProps.onCheck === nextProps.onCheck;
});

const DisasterChecklistDialog = ({ scenarioData, onCheckItem }) => {
  // 부모에서 전달받은 onCheckItem을 감싸서 다음 상태 계산 로직 수행
  const handleCheck = useCallback((step_id, current_checked_status) => {
    const next_status = (current_checked_status + 1) % 3; // 0 -> 1 -> 2 -> 0
    onCheckItem(step_id, next_status);
  }, [onCheckItem]);

  if (!scenarioData) return null;

  return (
    <>
      <style>{animations}</style>
      <FloatingPanel elevation={8}>
        <PanelHeader>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              animation: "blinking 1.5s infinite",
              willChange: "opacity", // 성능 최적화 힌트
            }}
          >
            <Icon
              name="fireIcon"
              style={{
                width: "28px",
                height: "28px",
                marginRight: "12px",
                color: "#f44336",
              }}
            />
            <Typography
              variant="h5"
              component="span"
              sx={{ fontWeight: "bold", color: customDarkTheme.palette.text.primary }}
            >
              재난 대응 SOP (화재 발생)
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ color: customDarkTheme.palette.text.secondary, mt: 1 }}
          >
            {`발생 시각: ${new Date(scenarioData.start_time).toLocaleString()}`}
          </Typography>
        </PanelHeader>
        <PanelContent>
          <List sx={{ p: 0 }}>
            {scenarioData.checklist.map((item) => (
              <ChecklistItem
                key={item.step_id}
                item={item}
                onCheck={handleCheck}
              />
            ))}
          </List>
        </PanelContent>
      </FloatingPanel>
    </>
  );
};

// 컴포넌트 전체 메모이제이션 (scenarioData가 변경되지 않으면 리렌더링 방지)
export default React.memo(DisasterChecklistDialog);
